function normalizeBaseUrl(baseUrl) {
  if (!baseUrl || typeof baseUrl !== 'string') {
    throw new Error('Education HTTP request baseUrl is required');
  }

  return baseUrl.replace(/\/$/, '');
}

function createRequestUrl(baseUrl, requestPath) {
  return `${normalizeBaseUrl(baseUrl)}/${String(requestPath || '').replace(/^\//, '')}`;
}

function isFormDataPayload(data) {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

function createHeaders({ context = {}, data, token }) {
  const headers = {
    Accept: 'application/json',
  };

  if (context.locale) {
    headers['Accept-Language'] = context.locale;
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (context.merchantId) {
    headers['X-Merchant-Id'] = context.merchantId;
  }

  if (data !== null && data !== undefined && !isFormDataPayload(data)) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

async function readResponseData(response) {
  if (typeof response?.json !== 'function') {
    return null;
  }

  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

export function createEducationHttpRequest({
  authTokenProvider,
  baseUrl,
  fetchImpl = globalThis.fetch,
  tokenRefreshProvider,
} = {}) {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);

  if (typeof authTokenProvider !== 'function') {
    throw new Error('Education HTTP request authTokenProvider is required');
  }

  if (typeof fetchImpl !== 'function') {
    throw new Error('Education HTTP request fetch implementation is required');
  }

  async function performRequest(endpointRequest, token) {
    const isFormData = isFormDataPayload(endpointRequest.data);
    const response = await fetchImpl(createRequestUrl(normalizedBaseUrl, endpointRequest.path), {
      body: endpointRequest.data === null || endpointRequest.data === undefined
        ? undefined
        : isFormData
          ? endpointRequest.data
          : JSON.stringify(endpointRequest.data),
      headers: createHeaders({
        context: endpointRequest.context,
        data: endpointRequest.data,
        token,
      }),
      method: endpointRequest.method,
    });
    const data = await readResponseData(response);

    if (!response.ok) {
      throw {
        response: {
          data,
          status: response.status,
        },
      };
    }

    return {
      data,
      status: response.status,
    };
  }

  return async function educationHttpRequest(endpointRequest = {}) {
    const token = await authTokenProvider({
      context: endpointRequest.context,
      methodName: endpointRequest.methodName,
      path: endpointRequest.path,
    });

    try {
      return await performRequest(endpointRequest, token);
    } catch (error) {
      const status = error.response?.status;
      const errorMessage = error.response?.data?.message;
      const isTokenError = status === 401 || errorMessage === 'invalid token';

      if (isTokenError && typeof tokenRefreshProvider === 'function') {
        try {
          await tokenRefreshProvider({
            context: endpointRequest.context,
            methodName: endpointRequest.methodName,
            path: endpointRequest.path,
          });

          const newToken = await authTokenProvider({
            context: endpointRequest.context,
            methodName: endpointRequest.methodName,
            path: endpointRequest.path,
          });

          return await performRequest(endpointRequest, newToken);
        } catch (refreshError) {
          throw error;
        }
      }

      throw error;
    }
  };
}
